import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Storefront, CheckCircle, XCircle } from "@phosphor-icons/react";

export default function Onboard() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (user?.store_id) nav("/admin");
  }, [user, nav]);

  const [form, setForm] = useState({
    name: "", slug: "", description: "",
    address: "", city: "", state: "", pincode: "", phone: "",
  });
  const [slugStatus, setSlugStatus] = useState(null); // null | true | false
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm({ ...form, [k]: v });

  useEffect(() => {
    if (!form.slug || form.slug.length < 3) { setSlugStatus(null); return; }
    const t = setTimeout(() => {
      api.get(`/stores/check-slug/${form.slug}`).then((r) => setSlugStatus(r.data.available));
    }, 350);
    return () => clearTimeout(t);
  }, [form.slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug) return toast.error("Store name & URL are required");
    if (slugStatus === false) return toast.error("Store URL is taken");
    setSubmitting(true);
    try {
      await api.post("/stores", form);
      const me = await api.get("/auth/me");
      setUser(me.data);
      localStorage.setItem("qd_user", JSON.stringify(me.data));
      toast.success("Store created! Welcome to QuickDukaan");
      nav("/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create store");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#F9F9F9] py-10 px-4">
      <div className="max-w-2xl mx-auto fade-up">
        <div className="text-center mb-8">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-[#F8CB46] items-center justify-center mb-4">
            <Storefront weight="fill" size={28} className="text-black" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Create your store</h1>
          <p className="text-gray-600 mt-2">Just a few details and you're live in minutes.</p>
        </div>

        <form onSubmit={submit} className="qd-card p-6 space-y-5">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Store name</label>
            <input data-testid="onboard-name" className="qd-input mt-2" value={form.name}
              onChange={(e) => {
                update("name", e.target.value);
                if (!form.slug) update("slug", e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, ""));
              }}
              placeholder="FreshMart Pitampura" />
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Store URL</label>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-gray-500 font-mono text-sm">quickdukaan.in/</span>
              <div className="relative flex-1">
                <input data-testid="onboard-slug" className="qd-input font-mono" value={form.slug}
                  onChange={(e) => update("slug", e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ""))}
                  placeholder="myshop" />
                {form.slug.length >= 3 && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {slugStatus === true && <CheckCircle weight="fill" size={20} className="text-green-600" />}
                    {slugStatus === false && <XCircle weight="fill" size={20} className="text-red-500" />}
                  </div>
                )}
              </div>
            </div>
            {slugStatus === false && <div className="text-xs text-red-500 mt-1">This URL is already taken</div>}
            {slugStatus === true && <div className="text-xs text-green-600 mt-1">✓ Available</div>}
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Tagline</label>
            <input data-testid="onboard-description" className="qd-input mt-2" value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="Fresh groceries in 20 minutes" />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Phone</label>
              <input data-testid="onboard-phone" className="qd-input mt-2" value={form.phone}
                onChange={(e) => update("phone", e.target.value)} placeholder="+91..." />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Pincode</label>
              <input data-testid="onboard-pincode" className="qd-input mt-2" value={form.pincode}
                onChange={(e) => update("pincode", e.target.value)} placeholder="110034" />
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Address</label>
            <input data-testid="onboard-address" className="qd-input mt-2" value={form.address}
              onChange={(e) => update("address", e.target.value)} placeholder="Shop no, street, locality" />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-gray-500">City</label>
              <input data-testid="onboard-city" className="qd-input mt-2" value={form.city}
                onChange={(e) => update("city", e.target.value)} placeholder="New Delhi" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-gray-500">State</label>
              <input data-testid="onboard-state" className="qd-input mt-2" value={form.state}
                onChange={(e) => update("state", e.target.value)} placeholder="Delhi" />
            </div>
          </div>

          <button type="submit" disabled={submitting}
            data-testid="onboard-submit"
            className="qd-btn-green w-full disabled:opacity-60">
            {submitting ? "Creating…" : "Create my store"}
          </button>
        </form>
      </div>
    </div>
  );
}
